import type { Message, Proposal } from "@playbookai/player-ai-client";

export type ThreadItem =
  | { readonly kind: "message"; readonly message: Message }
  | { readonly kind: "proposals"; readonly group: Proposal[] };

/** A Player AI response: an assistant message that says something. */
export function isPlayerAIResponse(message: Message): boolean {
  return (
    message.role === "assistant" &&
    typeof message.content.text === "string" &&
    message.content.text.trim().length > 0
  );
}

/**
 * Resolved canvas work precedes its response; work requiring action follows it.
 *
 * Messages keep conversation order. A Run's proposal sections and notices sit
 * in time order as before, except proposal sections are grouped around their
 * own Run's last response. Pending, applying and failed sections follow it;
 * applied and dismissed sections precede it, regardless of approval mode.
 * Updates are matched to their Run, so late work from an older turn is lifted
 * into THAT turn and never reads as part of a newer one. A Run with no
 * response yet leaves its sections where time puts them.
 */
export function orderThread(
  messages: readonly Message[],
  proposalGroups: readonly Proposal[][],
): ThreadItem[] {
  const ordered = [...messages].sort(
    (left, right) => left.sequence - right.sequence,
  );
  const lastResponse = new Map<string, Message>();
  for (const message of ordered) {
    if (message.run_id !== null && isPlayerAIResponse(message)) {
      lastResponse.set(message.run_id, message);
    }
  }
  const lifted = new Map<string, Array<{ at: number; item: ThreadItem }>>();
  const below = new Map<string, Array<{ at: number; item: ThreadItem }>>();
  const lift = (anchor: Message, at: number, item: ThreadItem): void => {
    const above = lifted.get(anchor.id) ?? [];
    above.push({ at, item });
    lifted.set(anchor.id, above);
  };

  const timed: Array<{ at: number; group: Proposal[] }> = [];
  for (const group of proposalGroups) {
    // A partially resolved batch must not hide remaining approval controls.
    const sections = new Map<string, Proposal[]>();
    for (const proposal of group) {
      const unresolved = ["pending", "applying", "failed"].includes(
        proposal.status,
      );
      const key = `${proposal.run_id}:${unresolved}`;
      const section = sections.get(key) ?? [];
      section.push(proposal);
      sections.set(key, section);
    }
    for (const section of sections.values()) {
      const at = Math.min(
        ...section.map((proposal) => Date.parse(proposal.created_at)),
      );
      const proposal = section[0]!;
      const anchor = lastResponse.get(proposal.run_id);
      if (!anchor) {
        timed.push({ at, group: section });
      } else if (["pending", "applying", "failed"].includes(proposal.status)) {
        const after = below.get(anchor.id) ?? [];
        after.push({ at, item: { kind: "proposals", group: section } });
        below.set(anchor.id, after);
      } else {
        lift(anchor, at, { kind: "proposals", group: section });
      }
    }
  }
  timed.sort((left, right) => left.at - right.at);

  const liftedMessageIds = new Set<string>();
  for (const message of ordered) {
    const anchor =
      message.run_id !== null ? lastResponse.get(message.run_id) : undefined;
    if (
      anchor &&
      message.role !== "user" &&
      !isPlayerAIResponse(message) &&
      message.sequence > anchor.sequence
    ) {
      lift(anchor, Date.parse(message.created_at), {
        kind: "message",
        message,
      });
      liftedMessageIds.add(message.id);
    }
  }

  const items: ThreadItem[] = [];
  let next = 0;
  for (const message of ordered) {
    if (liftedMessageIds.has(message.id)) continue;
    const at = Date.parse(message.created_at);
    // Ties keep the message first, as the timeline always has.
    while (next < timed.length && timed[next]!.at < at) {
      items.push({ kind: "proposals", group: timed[next]!.group });
      next += 1;
    }
    const above = lifted.get(message.id);
    if (above) {
      items.push(
        ...above
          .sort((left, right) => left.at - right.at)
          .map((entry) => entry.item),
      );
    }
    items.push({ kind: "message", message });
    const after = below.get(message.id);
    if (after) {
      items.push(
        ...after
          .sort((left, right) => left.at - right.at)
          .map((entry) => entry.item),
      );
    }
  }
  for (; next < timed.length; next += 1) {
    items.push({ kind: "proposals", group: timed[next]!.group });
  }
  return items;
}
